import type { PaymentRecord } from '../types';

const statusColor: Record<string, string> = {
  approved: 'bg-emerald-500/20 text-emerald-400',
  pending: 'bg-amber-500/20 text-amber-400',
  rejected: 'bg-red-500/20 text-red-400',
  refunded: 'bg-orange-500/20 text-orange-400',
  in_process: 'bg-cyan-500/20 text-cyan-400',
};

const statusLabel: Record<string, string> = {
  approved: 'Aprovado',
  pending: 'Pendente',
  rejected: 'Recusado',
  refunded: 'Estornado',
  in_process: 'Em análise',
};

const statusIcon: Record<string, string> = {
  approved: 'ri-shield-check-line',
  pending: 'ri-time-line',
  rejected: 'ri-close-circle-line',
  refunded: 'ri-arrow-go-back-line',
  in_process: 'ri-loader-3-line',
};

export default function PaymentsTab({ payments }: { payments: PaymentRecord[] }) {
  if (!payments.length) {
    return (
      <div className="bg-white/5 border border-white/8 rounded-2xl p-16 text-center">
        <i className="ri-bank-card-line text-4xl text-white/20 mb-3" />
        <p className="text-[14px] text-white/30 font-500">Nenhum pagamento registrado</p>
        <p className="text-[12px] text-white/20 mt-1">Os pagamentos do cliente aparecerão aqui</p>
      </div>
    );
  }

  const approved = payments.filter((p) => p.status === 'approved');
  const totalPaid = approved.reduce((acc, p) => acc + Number(p.amount), 0);
  const rejected = payments.filter((p) => p.status === 'rejected');
  const pending = payments.filter((p) => p.status === 'pending' || p.status === 'in_process');

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total pago', val: `R$ ${totalPaid.toFixed(2)}`, cls: 'text-emerald-400', icon: 'ri-money-dollar-circle-line', bg: 'bg-emerald-500/10 border-emerald-500/20' },
          { label: 'Aprovados', val: String(approved.length), cls: 'text-white', icon: 'ri-shield-check-line', bg: 'bg-white/5 border-white/8' },
          { label: 'Recusados', val: String(rejected.length), cls: 'text-red-400', icon: 'ri-close-circle-line', bg: 'bg-red-500/8 border-red-500/20' },
          { label: 'Pendentes/análise', val: String(pending.length), cls: 'text-amber-400', icon: 'ri-time-line', bg: 'bg-amber-500/8 border-amber-500/20' },
        ].map((item) => (
          <div key={item.label} className={`border rounded-xl p-4 text-center ${item.bg}`}>
            <i className={`${item.icon} text-[20px] ${item.cls} mb-2`} />
            <p className={`text-[20px] font-700 ${item.cls}`}>{item.val}</p>
            <p className="text-[11px] text-white/40 mt-1">{item.label}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white/5 border border-white/8 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10">
          <h3 className="text-[13px] font-600 text-white">Histórico de Pagamentos</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                {['Data', 'Plano', 'Valor', 'Status', 'Gateway', 'Transaction ID', 'Método'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3 text-[12px] text-white/60 whitespace-nowrap">
                    {new Date(p.created_at).toLocaleDateString('pt-BR')}
                    <span className="block text-[10px] text-white/30">
                      {new Date(p.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-white whitespace-nowrap">{p.plan_name || '—'}</td>
                  <td className="px-4 py-3 text-[13px] font-600 text-white whitespace-nowrap">R$ {Number(p.amount).toFixed(2)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`flex items-center gap-1.5 text-[11px] font-600 px-2 py-0.5 rounded-full w-fit ${statusColor[p.status] || 'bg-white/10 text-white/40'}`}>
                      <i className={statusIcon[p.status] || 'ri-question-line'} />
                      {statusLabel[p.status] || p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-white/50 whitespace-nowrap">{p.gateway || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-mono text-[10px] text-white/40 bg-white/5 px-2 py-0.5 rounded-lg max-w-[120px] block truncate">
                      {p.gateway_transaction_id || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-white/50 whitespace-nowrap">{p.payment_method || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
